import * as React from "react";
import { Loader2, Check, Inbox, Send, ArrowLeft } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  useAdminSupport, useResolveSupport, useAdminSupportThread, useReplySupport
} from "@/hooks/useAdmin";
import { createSupportConnection } from "@/lib/supportHub";

const FILTERS = [
  { key: "open", label: "Open" },
  { key: "", label: "All" },
  { key: "resolved", label: "Resolved" }
] as const;

export function AdminSupportPage() {
  const [status, setStatus] = React.useState<string>("open");
  const [selected, setSelected] = React.useState<number | undefined>(undefined);
  const { data: list, isLoading } = useAdminSupport(status);
  const qc = useQueryClient();

  // Live: refresh the inbox (and the open thread) on any customer activity.
  React.useEffect(() => {
    let disposed = false;
    const conn = createSupportConnection();
    conn.on("ticketActivity", (p: { ticket_id: number }) => {
      qc.invalidateQueries({ queryKey: ["admin-support"] });
      if (p?.ticket_id) qc.invalidateQueries({ queryKey: ["admin-support-thread", p.ticket_id] });
    });
    // Defer a tick so StrictMode's double-mount doesn't abort negotiation.
    const t = window.setTimeout(() => { if (!disposed) conn.start().catch(() => {}); }, 0);
    return () => { disposed = true; window.clearTimeout(t); conn.stop().catch(() => {}); };
  }, [qc]);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">Support inbox</h2>
        <p className="text-sm text-muted-foreground">Live chat with customers — replies are pushed instantly and emailed too.</p>
      </div>

      <div className="flex gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setStatus(f.key)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
              status === f.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
        {/* List */}
        <div className={`${selected ? "hidden lg:block" : ""} space-y-2`}>
          {isLoading ? (
            <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (list?.length ?? 0) === 0 ? (
            <Card><CardContent className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
              <Inbox className="h-8 w-8" /><p>No {status === "open" ? "open " : ""}conversations.</p>
            </CardContent></Card>
          ) : (
            list!.map((m) => (
              <button
                key={m.id}
                onClick={() => setSelected(m.id)}
                className={`w-full rounded-lg border p-3 text-left transition ${
                  selected === m.id ? "border-primary bg-brand-accent/5" : "border-border hover:border-primary/40"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium">{m.sender_name || m.sender_email}</span>
                  <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                    m.status === "resolved" ? "bg-brand-green/10 text-brand-green" : "bg-amber-500/10 text-amber-600"
                  }`}>{m.status}</span>
                </div>
                <p className="mt-1 truncate text-sm text-muted-foreground">{m.message}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {new Date(m.last_message_at ?? m.created_at).toLocaleString()}
                  {m.reply_count ? ` · ${m.reply_count} repl${m.reply_count === 1 ? "y" : "ies"}` : ""}
                </p>
              </button>
            ))
          )}
        </div>

        {/* Thread */}
        <div className={selected ? "" : "hidden lg:block"}>
          {selected ? (
            <ThreadPanel ticketId={selected} onBack={() => setSelected(undefined)} />
          ) : (
            <Card><CardContent className="flex h-full min-h-[300px] items-center justify-center text-sm text-muted-foreground">
              Select a conversation to view it.
            </CardContent></Card>
          )}
        </div>
      </div>
    </div>
  );
}

function ThreadPanel({ ticketId, onBack }: { ticketId: number; onBack: () => void }) {
  const { data: thread, isLoading } = useAdminSupportThread(ticketId);
  const reply = useReplySupport();
  const resolve = useResolveSupport();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [body, setBody] = React.useState("");
  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }));
  }, [thread]);

  async function onSend() {
    const text = body.trim();
    if (!text || reply.isPending) return;
    try {
      await reply.mutateAsync({ id: ticketId, body: text });
      setBody("");
      qc.invalidateQueries({ queryKey: ["admin-support-thread", ticketId] });
      qc.invalidateQueries({ queryKey: ["admin-support"] });
    } catch {
      toast({ title: "Couldn't send reply", variant: "destructive" });
    }
  }

  async function onResolve() {
    try {
      await resolve.mutateAsync(ticketId);
      toast({ title: "Marked resolved", variant: "success" });
    } catch {
      toast({ title: "Couldn't update", variant: "destructive" });
    }
  }

  return (
    <Card className="flex h-[560px] flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={onBack} aria-label="Back"><ArrowLeft className="h-4 w-4" /></Button>
          <span className="font-semibold">{thread?.messages[0]?.sender_name || "Conversation"}</span>
        </div>
        <Button variant="outline" size="sm" disabled={resolve.isPending || thread?.status === "resolved"} onClick={onResolve}>
          <Check className="h-4 w-4" /> Resolve
        </Button>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-2.5 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          thread?.messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender_type === "operator" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                m.sender_type === "operator" ? "rounded-br-sm bg-primary text-primary-foreground" : "rounded-bl-sm bg-muted text-foreground"
              }`}>
                <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider opacity-70">
                  {m.sender_type === "operator" ? (m.sender_name || "You") : (m.sender_name || "Customer")}
                </p>
                <p className="whitespace-pre-wrap">{m.body}</p>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="flex items-end gap-2 border-t border-border p-3">
        <textarea
          rows={1}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void onSend(); } }}
          placeholder="Type a reply…"
          className="max-h-24 flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        <Button size="icon" onClick={onSend} disabled={reply.isPending || !body.trim()} aria-label="Send reply">
          {reply.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </Card>
  );
}
